import { useSyncExternalStore } from 'react';

// Which reads are currently showing saved data. While Apigee is down
// fai-apigee-wrapper-svc answers from its database and marks the response with
// X-Data-Source: snapshot and X-Snapshot-Fetched-At (decision #51). The shell
// shows a notice until those same reads come back live. Tracked per path, so
// one environment recovering doesn't hide the notice for another that hasn't.
// A path nothing has re-read for a while is dropped on the next live answer,
// or leaving a page during the outage would hold the notice up forever.
const FORGET_AFTER_MS = 2 * 60_000;

const snapshots = new Map();
const listeners = new Set();
let oldestFetchedAt = null;

const publish = () => {
  const times = [...snapshots.values()].map((entry) => entry.fetchedAt).sort();
  const next = times[0] ?? null;
  if (next === oldestFetchedAt) return;
  oldestFetchedAt = next;
  listeners.forEach((listener) => listener());
};

const pathOf = (url) => {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return String(url);
  }
};

/** Call with every wrapper response. `getHeader` reads a response header by lower-case name. */
export const noteApigeeResponse = (url, getHeader) => {
  const source = getHeader('x-data-source');
  const path = pathOf(url);
  const now = Date.now();
  if (source === 'snapshot') {
    snapshots.set(path, { fetchedAt: getHeader('x-snapshot-fetched-at') || new Date(now).toISOString(), seenAt: now });
  } else if (source === 'live') {
    snapshots.delete(path);
    snapshots.forEach((entry, key) => {
      if (now - entry.seenAt > FORGET_AFTER_MS) snapshots.delete(key);
    });
  } else {
    return;
  }
  publish();
};

/** Response interceptor for the axios clients that reach the wrapper. */
export const noteAxiosApigeeResponse = (response) => {
  const { baseURL = '', url = '' } = response.config || {};
  noteApigeeResponse(`${baseURL}${url}`, (name) => response.headers?.[name]);
  return response;
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const getOldestFetchedAt = () => oldestFetchedAt;

/** ISO time of the oldest saved data on show, or null when everything is live. */
export const useApigeeSnapshotFetchedAt = () => useSyncExternalStore(subscribe, getOldestFetchedAt, getOldestFetchedAt);

export const resetApigeeSnapshotStatusForTests = () => {
  snapshots.clear();
  publish();
};
